import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { UserPlus, Search } from "lucide-react";

const EMPTY_IMG =
  "https://images.unsplash.com/photo-1686129947538-5a432de30493?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHx2aW50YWdlJTIwbmV3JTIweW9yayUyMGNpdHklMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg3MTYwNzI2fDA&ixlib=rb-4.1.0&q=85";

export default function Customers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/customers").then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, []);

  const isSales = user?.role === "sales";
  const fullName = (c) => [c.first_name, c.middle_name, c.last_name].filter(Boolean).join(" ");
  const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 })}`);

  const filtered = (rows || []).filter((c) => {
    const s = q.toLowerCase();
    return !s || fullName(c).toLowerCase().includes(s) || c.customer_id.toLowerCase().includes(s);
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl">Customers</h1>
          <p className="text-muted-foreground mt-1">
            {isSales ? "Limited view — names, sales & tickets." : "Full customer records."}
          </p>
        </div>
        {(user?.role === "admin" || user?.role === "sales") && (
          <Button data-testid="new-customer-btn" className="rounded-none" onClick={() => navigate("/customers/new")}>
            <UserPlus className="h-4 w-4 mr-2" /> New Customer
          </Button>
        )}
      </div>

      <div className="relative max-w-sm mb-4">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="customer-search"
          className="rounded-none pl-9"
          placeholder="Search by name or ID…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {rows === null ? (
        <p className="font-mono text-sm text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="border border-border bg-card p-10 text-center">
          <img src={EMPTY_IMG} alt="Empty" className="h-40 w-full object-cover grayscale mb-4" />
          <p className="text-muted-foreground">No customers on record yet.</p>
        </div>
      ) : (
        <div className="border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary hover:bg-secondary">
                <TableHead className="font-mono text-xs uppercase">Customer ID</TableHead>
                <TableHead className="font-mono text-xs uppercase">Name</TableHead>
                {!isSales && <TableHead className="font-mono text-xs uppercase">Contact</TableHead>}
                <TableHead className="font-mono text-xs uppercase">Converted</TableHead>
                <TableHead className="font-mono text-xs uppercase">Sale Amount</TableHead>
                <TableHead className="font-mono text-xs uppercase">Sale Status</TableHead>
                <TableHead className="font-mono text-xs uppercase">Signature</TableHead>
                <TableHead className="font-mono text-xs uppercase text-center">Tickets</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow
                  key={c.id}
                  data-testid={`customer-row-${c.customer_id}`}
                  className="cursor-pointer"
                  onClick={() => navigate(`/customers/${c.id}`)}
                >
                  <TableCell className="font-mono text-primary">{c.customer_id}</TableCell>
                  <TableCell className="font-medium">{fullName(c)}</TableCell>
                  {!isSales && <TableCell className="font-mono text-sm">{c.contact_number}</TableCell>}
                  <TableCell>
                    <StatusBadge value={c.call_converted ? "Yes" : "No"} />
                  </TableCell>
                  <TableCell className="font-mono">{money(c.sale_amount)}</TableCell>
                  <TableCell>{c.sale_status ? <StatusBadge value={c.sale_status} /> : "—"}</TableCell>
                  <TableCell><StatusBadge value={c.signature_status} /></TableCell>
                  <TableCell className="text-center font-mono">{c.ticket_count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
